import React, { useEffect, useState } from "react";
import { Search, ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/apiClient";
import StatusBadge from "../StatusBadge";

export default function Tokens() {
  const [d, setD] = useState(null);
  const [q, setQ] = useState("");
  const [state, setState] = useState("");
  const [scratched, setScratched] = useState("");
  const [sel, setSel] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q; if (state) params.state = state; if (scratched) params.scratched = scratched;
      const { data } = await api.get("/admin/tokens", { params });
      setD(data);
    } catch { toast.error("Could not load tokens"); } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [state, scratched]);

  if (sel) return <TokenDetail tokenId={sel} onBack={() => setSel(null)} />;

  return (
    <div className="space-y-5" data-testid="admin-tokens">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-heading text-2xl font-bold">Token management</h1>
        <div className="flex flex-wrap gap-2">
          <div className="flex items-center gap-2 rounded-full border border-slate-700 px-3 py-1.5">
            <Search className="h-4 w-4 text-slate-500" />
            <input data-testid="token-search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Token id / mobile / merchant" className="w-52 bg-transparent text-sm outline-none placeholder:text-slate-600" />
          </div>
          <select data-testid="token-filter-state" value={state} onChange={(e) => setState(e.target.value)} className="rounded-full border border-slate-700 bg-slate-900 px-3 py-1.5 text-sm">
            <option value="">All states</option><option value="ISSUED">Issued</option><option value="USED">Used</option>
          </select>
          <select data-testid="token-filter-scratched" value={scratched} onChange={(e) => setScratched(e.target.value)} className="rounded-full border border-slate-700 bg-slate-900 px-3 py-1.5 text-sm">
            <option value="">Any</option><option value="true">Scratched</option><option value="false">Unscratched</option>
          </select>
        </div>
      </div>

      {d && (
        <div className="flex flex-wrap gap-2 text-xs">
          <Chip label="Total" value={d.total} />
          <Chip label="Scratched" value={d.scratched} accent />
          <Chip label="Unscratched" value={d.unscratched} />
          {Object.entries(d.state_counts).map(([k, v]) => <Chip key={k} label={k} value={v} />)}
        </div>
      )}

      <div className="ledger-card overflow-hidden rounded-2xl">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="tokens-table">
            <thead className="bg-slate-900/50 font-mono text-xs uppercase tracking-wider text-slate-500">
              <tr><th className="p-4 text-left">Token</th><th className="p-4 text-left">Merchant</th><th className="p-4 text-left">Mobile</th><th className="p-4 text-left">State</th><th className="p-4 text-right">Cashback</th><th className="p-4 text-left">Created</th></tr>
            </thead>
            <tbody>
              {(d?.tokens || []).map((t) => (
                <tr key={t.tokenId} data-testid={`token-row-${t.tokenId}`} onClick={() => setSel(t.tokenId)} className="cursor-pointer border-t border-slate-800/60 hover:bg-white/5">
                  <td className="p-4 font-mono text-xs">{t.tokenId}</td>
                  <td className="p-4">{t.business_name}</td>
                  <td className="p-4 text-slate-400">{t.customer_mobile || "—"}</td>
                  <td className="p-4"><StatusBadge status={t.state === "USED" ? "USED" : "ACTIVE"} /></td>
                  <td className="p-4 text-right font-mono text-emerald-400">{t.cashback_display}</td>
                  <td className="p-4 text-slate-400">{new Date(t.created_at).toLocaleDateString("en-IN")}</td>
                </tr>
              ))}
              {loading && <tr><td colSpan={6} className="p-8 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-500" /></td></tr>}
              {!loading && d?.tokens?.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-slate-500">No tokens found.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function TokenDetail({ tokenId, onBack }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/admin/tokens/${tokenId}`).then((r) => setD(r.data)).catch(() => toast.error("Failed")); }, [tokenId]);
  if (!d) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-emerald-500" /></div>;
  const t = d.token;
  return (
    <div className="space-y-5" data-testid="admin-token-detail">
      <button data-testid="token-detail-back" onClick={onBack} className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white"><ArrowLeft className="h-4 w-4" /> Back to tokens</button>
      <div className="ledger-card rounded-2xl p-6">
        <p className="font-mono text-xs text-slate-500">{t.tokenId}</p>
        <p className="mt-1 font-heading text-3xl font-extrabold text-emerald-400">{t.cashback_display}</p>
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <KV k="State" v={t.state} />
          <KV k="Scratched" v={t.scratched ? "Yes" : "No"} />
          <KV k="Merchant" v={d.merchant?.business_name || t.merchant_id} />
          <KV k="Customer" v={d.customer?.name || t.customer_id} />
          <KV k="Mobile" v={t.customer_mobile || "—"} />
          <KV k="Campaign" v={t.campaignId} />
          <KV k="Created" v={new Date(t.created_at).toLocaleString("en-IN")} />
          <KV k="Scratched at" v={t.scratched_at ? new Date(t.scratched_at).toLocaleString("en-IN") : "—"} />
        </div>
      </div>
      {d.transaction && (
        <div className="ledger-card rounded-2xl p-6">
          <p className="font-mono text-[11px] uppercase tracking-widest text-slate-500">Cashback transaction</p>
          <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <KV k="Txn ID" v={d.transaction.txn_id} />
            <KV k="Amount" v={`₹${(d.transaction.amount_paise / 100).toLocaleString("en-IN")}`} />
            <KV k="Status" v={d.transaction.status} />
          </div>
        </div>
      )}
    </div>
  );
}

function Chip({ label, value, accent }) {
  return <span className="rounded-full bg-slate-900 px-3 py-1.5 font-mono"><span className={accent ? "text-emerald-400" : "text-slate-400"}>{label}</span> {value}</span>;
}
function KV({ k, v }) {
  return <div><p className="font-mono text-[11px] uppercase tracking-widest text-slate-500">{k}</p><p className="mt-0.5 text-sm">{v}</p></div>;
}
